import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { type ReactNode } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ReleaseToastVariant = 'success' | 'error';

interface ShowReleaseToastParams {
  variant?: ReleaseToastVariant;
  title: string;
  description: string;
  isMobile?: boolean;
  className?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  duration?: number;
  id?: string | number;
  onAction?: () => void;
  onDismiss?: () => void;
  actionLabel?: string;
  position?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-center'
    | 'bottom-center';
}

const VARIANT_ICON: Record<ReleaseToastVariant, ReactNode> = {
  success: <CircleCheck className="size-6 shrink-0 fill-green-500 text-white" />,
  error: <CircleAlert className="size-6 shrink-0 fill-destructive text-white" />,
};

const renderReleaseToast = (
  {
    variant,
    title,
    description,
    isMobile,
    onAction,
    actionLabel,
    descriptionClassName,
    contentClassName,
  }: ShowReleaseToastParams,
  onClose: () => void
) => {
  const isShowActionButton = !!onAction && !!actionLabel;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border bg-white p-4 shadow-md mr-6 w-[365px] max-[600px]:mr-0',
        {
          'm-0 w-85': isMobile,
        },
        contentClassName
      )}
    >
      <div className="flex items-center gap-2">
        {variant && VARIANT_ICON[variant]}
        <p className="flex-1 text-base font-semibold text-foreground">{title}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="shrink-0 cursor-pointer text-muted-foreground"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className={cn('flex flex-wrap items-center gap-3', descriptionClassName)}>
        <p className="text-sm text-muted-foreground">{description}</p>
        {isShowActionButton && (
          <Button
            variant="link"
            className="w-fit rounded-lg p-0 hover:no-underline h-5"
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

const showReleaseToast = (params: ShowReleaseToastParams) => {
  const { className, isMobile, id, duration, position, onDismiss } = params ?? {};

  const toastPosition = position ?? (isMobile ? 'bottom-center' : 'bottom-right');

  const toastId = id ?? crypto.randomUUID();

  return toast.custom((tId) => renderReleaseToast(params, () => toast.dismiss(tId)), {
    id: toastId,
    duration: duration ?? 5000,
    onDismiss,
    classNames: {
      toast: cn(
        'flex min-w-0! rounded-none! border-none! bg-transparent! p-0! shadow-none! max-h-none!',
        {
          'w-full! justify-center': isMobile,
          'w-auto! justify-end max-[600px]:w-full! max-[600px]:justify-center': !isMobile,
        },
        className
      ),
    },
    position: toastPosition,
  });
};

export default showReleaseToast;
